import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "./UserContext";
import { HiOutlineUserGroup, HiOutlineCog, HiOutlineSquares2X2, HiOutlineClipboardDocumentList, HiOutlineGlobeAlt, HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi2';
import { FaMapMarkerAlt } from 'react-icons/fa';
import CountryFlag from 'react-country-flag';
import { HiOutlineTrash } from 'react-icons/hi2';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../i18n';
import { COUNTRIES } from '../data/countries.js';
import { useActivityTracker } from './useActivityTracker';
// useSiteTour artık FloatingHelpButton içinde global olarak kullanılıyor
import CountrySelectModal from './CountrySelectModal';
import CitySelectModal from './CitySelectModal';

// BAYI_FIELDS ve BAYI_TABS component içinde tanımlanacak

// Yardımcı fonksiyon: Sayıları kısalt
function formatNumberShort(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toLocaleString('tr-TR', {maximumFractionDigits: 1}) + 'M';
  if (n >= 1_000) return (n / 1_000).toLocaleString('tr-TR', {maximumFractionDigits: 1}) + 'B';
  return n.toLocaleString('tr-TR');
}

// Yardımcı: Ürün adı -> dosya adı slug
function productImageSlug(name) {
  if (!name) return 'placeholder';
  
  let slug = name
    .toLocaleLowerCase('tr-TR')
    .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  // Boş slug kontrolü
  if (!slug || slug === '') return 'placeholder';
  
  return slug;
}

// Resim yükleme fonksiyonu - farklı formatları dener
function loadProductImage(productName, onError) {
  const slug = productImageSlug(productName);
  const formats = ['jpg', 'jpeg', 'png', 'webp'];
  
  function tryNextFormat(index) {
    if (index >= formats.length) {
      // Tüm formatlar denendi, placeholder kullan
      onError();
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      // Resim bulundu, src'yi güncelle
      const event = { target: { src: `/urunler/${slug}.${formats[index]}` } };
      onError(event);
    };
    img.onerror = () => {
      // Bu format bulunamadı, sonrakini dene
      tryNextFormat(index + 1);
    };
    img.src = `/urunler/${slug}.${formats[index]}`;
  }
  
  tryNextFormat(0);
}

export default function LoginPage({ onLogin }) {
  const { t } = useTranslation();
  const { login, user } = useUser();
  const { trackLogin, trackPageView } = useActivityTracker();
  // Tur artık FloatingHelpButton tarafından yönetiliyor
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [showBayiModal, setShowBayiModal] = useState(false);
  const [bayiStep, setBayiStep] = useState(1);
  const [bayiForm, setBayiForm] = useState({});
  const [bayiSozlesmeOnay, setBayiSozlesmeOnay] = useState(false);
  const [bayiFiles, setBayiFiles] = useState([]);
  const [bayiSuccess, setBayiSuccess] = useState(false);
  const [bayiTab, setBayiTab] = useState(0);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  // Geçici şifre değiştirme
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pwChangeNew, setPwChangeNew] = useState('');
  const [pwChangeConfirm, setPwChangeConfirm] = useState('');
  const [pwChangeError, setPwChangeError] = useState('');
  const [pwChangeLoading, setPwChangeLoading] = useState(false);
  const [pwChangePendingUser, setPwChangePendingUser] = useState(null);
  const [forgotSent, setForgotSent] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showSozlesmeModal, setShowSozlesmeModal] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  
  // Ülke ve şehir seçimi için state'ler
  const [ulke, setUlke] = useState('TR');
  const [sehir, setSehir] = useState('');
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);

  // Ürünler için state'ler
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(false);
  const [totalProductsCount, setTotalProductsCount] = useState(0);

  const LANGS = [
    { code: 'en', label: 'English' },
    { code: 'tr', label: 'Türkçe' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
    { code: 'it', label: 'Italiano' },
    { code: 'pt', label: 'Português' },
    { code: 'ru', label: 'Русский' },
    { code: 'zh', label: '中文' },
    { code: 'ja', label: '日本語' },
    { code: 'ar', label: 'العربية' },
    { code: 'hi', label: 'हिन्दी' },
    { code: 'ko', label: '한국어' },
    { code: 'nl', label: 'Nederlands' },
    { code: 'sv', label: 'Svenska' },
    { code: 'pl', label: 'Polski' },
    { code: 'uk', label: 'Українська' },
    { code: 'fa', label: 'فارسی' },
    { code: 'el', label: 'Ελληνικά' },
    { code: 'he', label: 'עברית' },
    { code: 'id', label: 'Bahasa Indonesia' },
  ];
  const [langDropdown, setLangDropdown] = useState(false);

  // BAYI_FIELDS ve BAYI_TABS çeviri ile tanımla
  const BAYI_FIELDS = [
    { name: 'firma', label: t('Firma / Sağlık Profesyoneli'), type: 'text', required: true },
    { name: 'yetkili', label: t('Yetkili Kişi'), type: 'text', required: true },
    { name: 'telefon', label: t('Telefon'), type: 'tel', required: true },
    { name: 'email', label: t('E-posta'), type: 'email', required: true },
    { name: 'sektor', label: t('Faaliyet Alanı / Sektör'), type: 'text', required: true },
    { name: 'sicil', label: t('Ticaret Sicil No'), type: 'text', required: false },
    { name: 'web', label: t('Web Sitesi'), type: 'text', required: false },
  ];

  const BAYI_TABS = [
    {
      name: t('Temel Bilgiler'),
      fields: ['firma', 'yetkili', 'telefon', 'email'],
    },
    {
      name: t('Ek Bilgiler'),
      fields: ['sektor', 'sicil', 'web'],
    },
  ];

  // Sayfa görüntüleme takibi
  useEffect(() => {
    trackPageView('login_page');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Ürünleri API'den çek - Her kategoriden 5'er tane
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        setProductsError(false);
        
        // Önce tüm ürünleri al (toplam sayı için)
        const allProductsResponse = await fetch('/api/products?limit=10000');
        if (!allProductsResponse.ok) {
          setProductsError(true);
          return;
        }
        
        const allProducts = await allProductsResponse.json();
        setTotalProductsCount(allProducts.length); // Toplam ürün sayısını kaydet
        
        // Kategorileri grupla
        const productsByCategory = {};
        allProducts.forEach(product => {
          if (product.kategori) {
            if (!productsByCategory[product.kategori]) {
              productsByCategory[product.kategori] = [];
            }
            productsByCategory[product.kategori].push(product);
          }
        });
        
        // Her kategoriden 5'er tane al
        const balancedProducts = [];
        Object.keys(productsByCategory).forEach(category => {
          const categoryProducts = productsByCategory[category];
          const selectedProducts = categoryProducts.slice(0, 5); // Her kategoriden 5 tane
          balancedProducts.push(...selectedProducts);
        });
        
        // measures alanını JSON parse et (SUT bazlı obje)
        const parsedProducts = balancedProducts.map(p => ({
          ...p,
          measures: (() => {
            if (!p.measures) return {};
            if (typeof p.measures === 'object' && !Array.isArray(p.measures)) return p.measures;
            try {
              const parsed = JSON.parse(p.measures);
              if (Array.isArray(parsed)) return { 'Genel': parsed };
              return parsed;
            } catch { return {}; }
          })()
        }));
        setProducts(parsedProducts);
      } catch (error) {
        console.error('Ürünler yüklenirken hata:', error);
        setProductsError(true);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      if (res.ok) {
        const user = await res.json();
        trackLogin({ email, firma: user.firma, role: user.rol, source: 'login_page' });
        if (user.must_change_password) {
          setPwChangePendingUser(user);
          setShowPasswordChange(true);
          setLoading(false);
          return;
        }
        // JWT token'ı ayrı sakla
        if (user.token) {
          localStorage.setItem("authToken", user.token);
        }
        localStorage.setItem("user", JSON.stringify(user));
        if (onLogin) onLogin({ ...user, role: user.rol });
        if (login) login({ ...user, role: user.rol });
        navigate("/dash");
      } else {
        const data = await res.json();
        if (res.status === 403) {
          // Onay durumu mesajları
          setError(data.error);
        } else {
          setError(data.error || "E-posta veya şifre hatalı.");
        }
      }
    } catch (err) {
      setError("Sunucuya bağlanılamadı.");
    }
    setLoading(false);
  };

  // Bayi başvuru formu gönderimi
  const handleBayiFormSubmit = async (e) => {
    e.preventDefault();
    if (bayiTab === 0) {
      setBayiTab(1);
      return;
    }
    if (!bayiSozlesmeOnay) return;
    setBayiStep(2);
    try {
      const res = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...bayiForm,
          ulke,
          sehir,
          password: bayiForm.sifre || '123456', // Şifre alanı eklenmemişse default ver
        })
      });
      if (res.ok) {
        setTimeout(() => {
          setBayiStep(3);
        }, 1200);
      } else {
        const data = await res.json();
        setError(data.error || 'Kayıt başarısız.');
        setBayiStep(1);
      }
    } catch (err) {
      setError('Sunucuya bağlanılamadı.');
      setBayiStep(1);
    }
  };
  // Dosya yükleme
  const handleBayiFileChange = (e) => {
    setBayiFiles([...e.target.files]);
  };
  // Başvuru tamamla
  const handleBayiComplete = () => {
    setBayiSuccess(true);
    setShowBayiModal(false);
    setBayiStep(1);
    setBayiForm({});
    setBayiFiles([]);
    setBayiSozlesmeOnay(false);
    setBayiTab(0);
    setUlke('TR');
    setSehir('');
  };

  // Teklif Talebi Formu için state
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedMeasure, setSelectedMeasure] = useState('');
  const selectedProduct = products.find(p => p.id === Number(selectedProductId));
  const measureOptions = selectedProduct ? (selectedProduct.measures || ['Standart']) : [];

  // Teklif Talebi Oluşturma Formu görünürlüğü için state
  const [showTeklifTalepForm, setShowTeklifTalepForm] = useState(false);

  // Teklif Talebi Oluşturma Formu için satır tabanlı state ve fonksiyonlar
  // Taslak yüklemesi
  const [talepRows, setTalepRows] = useState(() => {
    const draft = localStorage.getItem('teklifTalepTaslak');
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch {}
    }
    return [{ productId: '', measure: '', qty: '', desc: '', showDropdown: false }];
  });

  function handleAddRow() {
    setTalepRows([...talepRows, { productId: '', measure: '', qty: '', desc: '', showDropdown: false }]);
  }
  function handleRemoveRow(idx) {
    setTalepRows(talepRows.length === 1 ? [{ productId: '', measure: '', qty: '', desc: '', showDropdown: false }] : talepRows.filter((_, i) => i !== idx));
  }
  function handleRowChange(idx, field, value) {
    setTalepRows(talepRows.map((row, i) => {
      if (i !== idx) return row;
      if (field === 'productId') {
        return { ...row, productId: Number(value), measure: '', showDropdown: false };
      }
      return { ...row, [field]: value, showDropdown: false };
    }));
  }

  // handleTeklifTalepSubmit fonksiyonunu güncelle
  function handleTeklifTalepSubmit(e) {
    e.preventDefault();
    // Basit validasyon: en az bir satırda ürün ve ölçü seçili olmalı
    if (!talepRows.some(row => row.productId && row.measure && row.qty)) {
      alert('Lütfen en az bir satırda ürün, ölçü ve miktar girin.');
      return;
    }
    // Burada backend'e gönderme veya başka işlem yapılabilir
    alert('Talep gönderildi!\n' + talepRows.map((row, i) => `${i + 1}. Ürün: ${products.find(p => p.id === Number(row.productId))?.name || ''}, Ölçü: ${row.measure}, Miktar: ${row.qty}, Açıklama: ${row.desc}`).join('\n'));
    setShowTeklifTalepForm(false);
    setTalepRows([{ productId: '', measure: '', qty: '', desc: '', showDropdown: false }]);
    localStorage.removeItem('teklifTalepTaslak');
  }

  // İstatistik ikonları
  const statsIcons = [
    <HiOutlineUserGroup key="partner" className="w-7 h-7 md:w-8 md:h-8 text-[#143b6b] mb-2" />,
    <HiOutlineCog key="uretim" className="w-7 h-7 md:w-8 md:h-8 text-[#143b6b] mb-2" />,
    <HiOutlineSquares2X2 key="brans" className="w-7 h-7 md:w-8 md:h-8 text-[#143b6b] mb-2" />,
    <HiOutlineClipboardDocumentList key="siparis" className="w-7 h-7 md:w-8 md:h-8 text-[#143b6b] mb-2" />
  ];

  // Formun referansı
  const teklifTalepFormRef = useRef(null);

  // Butondan gelen event ile formu aç
  useEffect(() => {
    function handleShowFormEvent(e) {
      if (e.data && e.data.type === 'SHOW_TEKLIF_TALEP_FORM') {
        setShowTeklifTalepForm(true);
        setTimeout(() => {
          if (teklifTalepFormRef.current) {
            teklifTalepFormRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
    window.addEventListener('message', handleShowFormEvent);
    return () => window.removeEventListener('message', handleShowFormEvent);
  }, []);

  // Popup açıkken arka plan scroll'unu engelle
  useEffect(() => {
    if (showTeklifTalepForm) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [showTeklifTalepForm]);

  // Her değişiklikte taslağı kaydet
  useEffect(() => {
    localStorage.setItem('teklifTalepTaslak', JSON.stringify(talepRows));
  }, [talepRows]);

  // Yönetici için Yarım Kalanlar modalı
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [draftRows, setDraftRows] = useState([]);

  function openDraftsModal() {
    const draft = localStorage.getItem('teklifTalepTaslak');
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (Array.isArray(parsed) && parsed.length > 0) setDraftRows(parsed);
        else setDraftRows([]);
      } catch { setDraftRows([]); }
    } else {
      setDraftRows([]);
    }
    setShowDraftsModal(true);
  }

  // Ürün carousel için state'ler
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Kategorileri al
  const categories = [...new Set(products.map(p => p.kategori))].filter(Boolean);

  // Popüler ürünleri filtrele
  const getFilteredProducts = () => {
    let filtered = products;
    if (selectedCategory !== 'all') {
      filtered = products.filter(p => p.kategori === selectedCategory);
    }
    return filtered; // Tüm filtrelenmiş ürünleri göster
  };

  // Belirli sıralamaya göre ürünleri al
  const getProductsFromAllCategories = () => {
    // Ophthalmology kategorisindeki ürünleri hariç tut
    const otherProducts = products.filter(product => 
      product.kategori !== 'Ophthalmology'
    );
    
    // Kategorileri grupla
    const categoryProducts = {};
    otherProducts.forEach(product => {
      if (!categoryProducts[product.kategori]) {
        categoryProducts[product.kategori] = [];
      }
      categoryProducts[product.kategori].push(product);
    });
    
    // İstenen sıralama
    const priorityCategories = [
      'Neurovascular',
      'Cardiac', 
      'Arterial',
      'Venous',
      'Oncology',
      'General Surgery'
    ];
    
    // Sıralı ürünleri topla
    const selectedProducts = [];
    
    // Önce öncelikli kategorileri ekle
    priorityCategories.forEach(category => {
      if (categoryProducts[category]) {
        const categoryItems = categoryProducts[category].slice(0, 5); // Her kategoriden 5 ürün
        selectedProducts.push(...categoryItems);
      }
    });
    
    // Sonra diğer kategorileri ekle
    Object.keys(categoryProducts).forEach(category => {
      if (!priorityCategories.includes(category)) {
        const categoryItems = categoryProducts[category].slice(0, 5);
        selectedProducts.push(...categoryItems);
      }
    });
    
    // İlk 30 ürünü döndür
    return selectedProducts.slice(0, 30);
  };

  const popularProducts = getFilteredProducts();
  const catalogProducts = getProductsFromAllCategories();

  // Carousel otomatik geçiş
  useEffect(() => {
    if (popularProducts.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentProductIndex((prev) => (prev + 1) % popularProducts.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [popularProducts.length]);

  // Kategori değiştiğinde index'i sıfırla
  useEffect(() => {
    setCurrentProductIndex(0);
  }, [selectedCategory]);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCategoryDropdown && !event.target.closest('.relative')) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryDropdown]);

  // Carousel navigasyon - Yatay scroll için
  const nextProduct = () => {
    const container = document.querySelector('.overflow-x-auto');
    if (container) {
      // İlk kartı bul ve genişliğini hesapla
      const firstCard = container.querySelector('.flex-shrink-0');
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 12; // gap-3 = 12px
        const scrollAmount = cardWidth + gap;
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    }
  };

  const prevProduct = () => {
    const container = document.querySelector('.overflow-x-auto');
    if (container) {
      // İlk kartı bul ve genişliğini hesapla
      const firstCard = container.querySelector('.flex-shrink-0');
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 12; // gap-3 = 12px
        const scrollAmount = cardWidth + gap;
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <div className="min-h-screen flex flex-col bg-gradient-to-r from-[#e0e7ff] via-[#f7f8fa] to-[#c7d2fe] animate-gradient-move relative overflow-hidden">
        <style>{`
          @keyframes gradient-move {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .animate-gradient-move {
            background-size: 200% 200%;
            animation: gradient-move 12s ease-in-out infinite;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          
          /* Carousel şeffaflık efekti */
          .carousel-container {
            mask: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
            -webkit-mask: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
          }
          
          /* Ürün kartları için şeffaflık */
          .product-card {
            transition: opacity 0.3s ease;
          }
          
          .product-card.fade-edge {
            opacity: 0.3;
          }
          
          .product-card.fade-center {
            opacity: 1;
          }
        `}</style>
        {/* NAVBAR */}
        <nav className="w-full bg-white px-0 py-0 shadow-sm border-b border-[#e3e6ef] flex items-center justify-between h-14 md:h-16" data-tour="login-header">
          <div className="flex items-center gap-3 h-full pl-3 md:pl-6">
            <img src="/invamedorder.svg" alt="INVAMED Order" className="h-7 md:h-10 w-auto" style={{objectFit:'contain'}} />
          </div>
          <div className="flex items-center gap-2 pr-3 md:pr-6 relative">
            {/* Yöneticiye özel Yarım Kalanlar menüsü */}
            {user?.role === 'admin' && (
              <button className="px-4 py-1.5 border border-blue-700 text-blue-700 rounded-lg font-semibold hover:bg-blue-700 hover:text-white transition text-sm mr-2" onClick={openDraftsModal}>Yarım Kalanlar</button>
            )}
            <div className="relative">
              <button className="flex items-center gap-1 px-2 md:px-3 py-1.5 border border-[#e3e6ef] text-[#143b6b] rounded-lg font-semibold bg-white hover:bg-[#e3e6ef] transition text-sm" onClick={() => setLangDropdown(v => !v)}>
                <HiOutlineGlobeAlt className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden md:inline">{(LANGS.find(l => l.code === lang)?.label) || t('dil')}</span>
                <span className="md:hidden text-xs font-bold">{lang.toUpperCase()}</span>
                <svg className={`w-3 h-3 transition-transform ${langDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {langDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-[#e3e6ef] rounded-lg shadow-lg z-50 p-2">
                  <div className="grid grid-cols-3 gap-2">
                    {LANGS.map(l => (
                      <button
                        key={l.code}
                        className={`flex items-center justify-center px-2 py-2 rounded-md text-[#143b6b] font-medium border border-transparent hover:bg-[#e3e6ef] transition text-sm ${lang === l.code ? 'bg-[#e3e6ef] border-[#143b6b] font-bold' : ''}`}
                        onClick={() => { setLang(l.code); changeLanguage(l.code); setLangDropdown(false); }}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button className="px-3 md:px-4 py-1.5 border border-[#143b6b] text-[#143b6b] rounded-lg font-semibold hover:bg-[#143b6b] hover:text-white transition text-xs md:text-sm" onClick={() => setShowBayiModal(true)} data-tour="register-tab">{t('Üye Ol')}</button>
          </div>
        </nav>
                {/* ANA ALAN - 2 SÜTUNLU DÜZEN */}
        <div className="flex-1 flex flex-col lg:flex-row w-full min-h-screen max-w-5xl mx-auto">
          {/* SOL: LOGIN FORM */}
          <div className="w-full lg:w-1/2 flex items-center justify-center py-8 lg:py-0 lg:min-h-screen px-6">
            <div className="w-full max-w-sm bg-white/70 backdrop-blur-sm rounded-2xl shadow-2xl border border-[#e3e6ef] p-8" data-tour="login-form">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-[#143b6b] mb-2">{t('Giriş Yap')}</h2>
                <p className="text-sm text-[#4b587c]">{t('Hesabınıza giriş yapın')}</p>
              </div>
              
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#143b6b] mb-1">{t('E-posta')}</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 rounded-lg border border-[#e3e6ef] focus:ring-2 focus:ring-[#143b6b] text-[#1e2a44] transition placeholder:text-[#bfc9e0]"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="username"
                    placeholder={t('E-posta')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#143b6b] mb-1">{t('Şifre')}</label>
                  <div className="relative flex items-center">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="w-full px-4 py-3 rounded-lg border border-[#e3e6ef] focus:ring-2 focus:ring-[#143b6b] text-[#1e2a44] transition placeholder:text-[#bfc9e0]"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      placeholder={t('Şifre')}
                    />
                    <button
                      type="button"
                      className="absolute right-3 text-[#143b6b] text-xl select-none"
                      tabIndex={-1}
                      onClick={() => setShowPassword(v => !v)}
                      aria-label="Şifreyi Göster/Gizle"
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 2.25 12c2.036 3.807 6.07 6.75 9.75 6.75 1.772 0 3.68-.57 5.396-1.64M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" /></svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12S6.214 5.25 12 5.25 21.75 12 21.75 12 17.786 18.75 12 18.75 2.25 12 2.25 12Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <button type="button" className="text-xs text-[#4b587c] font-semibold hover:underline" tabIndex={-1} onClick={() => setShowForgotModal(true)}>{t('Şifremi Unuttum')}</button>
                  <a href="#" className="text-xs text-[#4b587c] font-semibold hover:underline" onClick={e => { e.preventDefault(); setShowHelpModal(true); }}>{t('Yardım')}</a>
                </div>
                {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm text-center animate-shake">{error}</div>}
                <button
                  type="submit"
                  className="mt-2 w-full px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-lg shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition text-base disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2"><span className="loader w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span> {t('Giriş yapılıyor...')}</span>
                  ) : (
                    t('Giriş Yap')
                  )}
                </button>
                <div className="text-xs text-[#4b587c] text-center mt-2 select-all">
                  {t('Üye Değil Misiniz?')} <a href="#" className="text-[#143b6b] font-semibold hover:underline" onClick={e => { e.preventDefault(); setShowBayiModal(true); }}>{t('Şimdi Üye Ol')}</a>
                </div>
              </form>
              <div className="text-[10px] text-[#4b587c] mt-4 text-center">
                This site is protected by reCAPTCHA and the Google <a href="#" className="underline">Privacy Policy</a> and <a href="#" className="underline">Terms of Service</a> apply.
              </div>
            </div>
          </div>
          
          {/* SAĞ: MİSAFİR ALANI */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center items-center relative px-6 py-8 lg:py-0 lg:min-h-screen">
            <div className="w-full max-w-md flex flex-col items-center justify-center">
              {/* Misafir Badge */}
              <div className="flex items-center gap-2 mb-6 cursor-pointer" onClick={() => navigate('/teklif-olustur')}>
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                  👤 {t('Misafir Kullanıcı')}
                </div>
                <div className="text-xs text-[#4b587c] bg-white/70 px-3 py-1 rounded-full hover:bg-white hover:shadow transition-all duration-300">
                  {t('Hızlı Sipariş')}
                </div>
              </div>

              {/* Ana Başlık */}
             
              <div className="text-base lg:text-lg text-[#4b587c] mb-6 text-center">
                {t('Giriş yapmadan hızlıca ürünlerinizi seçin ve siparişinizi oluşturun')}
              </div>

              {/* Özellikler Listesi */}
              <div className="w-full mb-6">
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-[#e3e6ef]">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold text-[#143b6b] text-sm">{t('Hızlı Sipariş')}</div>
                      <div className="text-xs text-[#4b587c]">{t('Giriş yapmadan sipariş oluşturun')}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-[#e3e6ef]">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold text-[#143b6b] text-sm">{t('Anında Erişim')}</div>
                      <div className="text-xs text-[#4b587c]">{t('Tüm ürünlere anında erişim')}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-[#e3e6ef]">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold text-[#143b6b] text-sm">{t('Güvenli İşlem')}</div>
                      <div className="text-xs text-[#4b587c]">{t('SSL korumalı güvenli sipariş')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Misafir Sipariş Butonu */}
              <button 
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-[#143b6b] to-[#1e2a44] text-white font-bold text-base shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3"
                onClick={() => navigate('/teklif-olustur')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t('Misafir Olarak Sipariş Oluştur')}
              </button>

              {/* Alt Bilgi */}
              <div className="text-xs text-[#4b587c] text-center mt-4">
                <div className="mb-2">💡 <strong>{t('İpucu:')}</strong> {t('Daha fazla özellik için üye olun')}</div>
                <div className="flex items-center justify-center gap-4 text-xs">
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    {t('Ücretsiz')}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    {t('Hızlı')}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    {t('Güvenli')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ürün slider ve detay modalı kaldırıldı */}

        {/* Yükleme animasyonu için ekstra stil */}
        <style>{`
          .loader { border-width: 2px; border-style: solid; border-radius: 9999px; border-color: #fff #fff #fff transparent; }
          @keyframes animate-shake { 10%, 90% { transform: translateX(-1px); } 20%, 80% { transform: translateX(2px); } 30%, 50%, 70% { transform: translateX(-4px); } 40%, 60% { transform: translateX(4px); } }
          .animate-shake { animation: animate-shake 0.5s; }
        `}</style>
        {/* Bayi Başvuru Modalı */}
        {showBayiModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-0 w-full max-w-xl max-h-[90vh] flex flex-col border border-[#e3e6ef] relative overflow-hidden">
              <button className="absolute top-4 right-4 text-[#143b6b] text-2xl font-bold z-10" onClick={() => {
                setShowBayiModal(false);
                setBayiStep(1);
                setBayiForm({});
                setBayiSozlesmeOnay(false);
                setBayiFiles([]);
                setBayiTab(0);
                setUlke('TR');
                setSehir('');
              }}>&times;</button>
              {bayiStep === 1 && (
                <form onSubmit={handleBayiFormSubmit} className="flex flex-col h-full overflow-y-auto">
                  <div className="flex-shrink-0 px-8 pt-8 pb-2">
                    <div className="text-2xl font-bold text-[#143b6b] mb-2">{t('bayiBasvuru')}</div>
                    <div className="flex gap-2 mb-4">
                      {BAYI_TABS.map((tab, idx) => (
                        <button type="button" key={tab.name} className={`flex-1 py-2 rounded-lg font-semibold text-base transition border ${bayiTab === idx ? 'bg-[#143b6b] text-white border-[#143b6b]' : 'bg-[#e3e6ef] text-[#143b6b] border-[#e3e6ef]'}`} onClick={() => setBayiTab(idx)}>{tab.name}</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto px-8 pb-8 pt-0">
                    {/* Ülke ve şehir seçimi - sadece ilk tab'da göster */}
                    {bayiTab === 0 && (
                      <>
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-[#143b6b] mb-1">{t('Ülke Seçimi')}</label>
                          <button
                            type="button"
                            className="w-full px-4 py-2 rounded-lg border border-[#e3e6ef] focus:ring-2 focus:ring-[#143b6b] text-[#1e2a44] transition bg-white flex items-center justify-between"
                            onClick={() => setShowCountryModal(true)}
                          >
                            <span>{COUNTRIES.find(c => c.code === ulke)?.name || t('Ülke Seçiniz')}</span>
                            <HiOutlineGlobeAlt className="ml-2 text-[#143b6b]" />
                          </button>
                          <CountrySelectModal
                            open={showCountryModal}
                            onClose={() => setShowCountryModal(false)}
                            onSelect={code => {
                              setUlke(code);
                              setSehir(''); // Ülke değiştiğinde şehir seçimini sıfırla
                            }}
                            currentCountry={ulke}
                          />
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-[#143b6b] mb-1">{t('Şehir Seçimi')}</label>
                          <button
                            type="button"
                            className="w-full px-4 py-2 rounded-lg border border-[#e3e6ef] focus:ring-2 focus:ring-[#143b6b] text-[#1e2a44] transition bg-white flex items-center justify-between"
                            onClick={() => setShowCityModal(true)}
                            disabled={!ulke}
                          >
                            <span>{sehir || t('Şehir Seçiniz')}</span>
                            <FaMapMarkerAlt className="ml-2 text-[#143b6b]" />
                          </button>
                          <CitySelectModal
                            open={showCityModal}
                            onClose={() => setShowCityModal(false)}
                            onSelect={city => setSehir(city)}
                            currentCity={sehir}
                            countryCode={ulke}
                          />
                        </div>
                      </>
                    )}
                    {BAYI_FIELDS.filter(f => BAYI_TABS[bayiTab].fields.includes(f.name)).map(field => (
                      <div key={field.name} className="mb-4">
                        <label className="block text-sm font-semibold text-[#143b6b] mb-1">
                          {t('applyFields')[field.name] || field.label || field.name}
                        </label>
                        <input
                          type={field.type}
                          className="w-full px-4 py-2 rounded-lg border border-[#e3e6ef] focus:ring-2 focus:ring-[#143b6b] text-[#1e2a44] transition placeholder:text-[#bfc9e0]"
                          value={bayiForm[field.name] || ''}
                          onChange={e => setBayiForm(f => ({ ...f, [field.name]: e.target.value }))}
                          required={field.required}
                          placeholder={t('applyFields')[field.name] || field.label || field.name}
                        />
                      </div>
                    ))}
                    {bayiTab === 1 && (
                      <div className="mt-4">
                        <label className="flex items-start gap-2">
                          <input 
                            type="checkbox" 
                            checked={bayiSozlesmeOnay} 
                            onChange={e => setBayiSozlesmeOnay(e.target.checked)} 
                            required 
                            className="mt-1"
                          />
                          <div className="text-xs text-[#4b587c]">
                            <span>{t('partnerlikSozlesmeOnay')}</span>
                            <button 
                              type="button" 
                              className="text-[#143b6b] font-semibold hover:underline ml-1"
                              onClick={() => setShowSozlesmeModal(true)}
                            >
                              {t('sozlesmeyiGoruntule')}
                            </button>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 flex gap-2 px-8 pb-8 pt-2 bg-white border-t border-[#e3e6ef]">
                    {bayiTab > 0 && (
                      <button type="button" className="flex-1 px-4 py-3 rounded-lg bg-[#e3e6ef] text-[#143b6b] font-bold text-base border border-[#e3e6ef] hover:bg-[#e3e6ef] transition" onClick={() => setBayiTab(bayiTab - 1)}>{t('geri')}</button>
                    )}
                    <button type="submit" className="flex-1 px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-base shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition disabled:opacity-60 disabled:cursor-not-allowed" disabled={bayiTab === 1 && !bayiSozlesmeOnay}>{bayiTab === BAYI_TABS.length - 1 ? t('basvuruGonder') : t('ileri')}</button>
                  </div>
                </form>
              )}
              {bayiStep === 2 && (
                <div className="flex flex-col items-center gap-4">
                  <div className="text-xl font-bold text-[#143b6b]">{t('sozlesmeGonderildi')}</div>
                  <div className="text-[#4b587c] text-center">{t('sozlesmeDesc')}</div>
                  <input type="file" multiple className="mt-2" onChange={handleBayiFileChange} />
                  <button className="mt-4 w-full px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-lg shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition text-base" onClick={handleBayiComplete} disabled={bayiFiles.length === 0}>{t('tamamla')}</button>
                </div>
              )}
              {bayiStep === 3 && (
                <div className="flex flex-col items-center gap-4">
                  <div className="text-2xl font-bold text-[#143b6b]">{t('basvuruAlindi')}</div>
                  <div className="text-[#4b587c] text-center">{t('basvuruAlindiDesc')}</div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="text-yellow-800 font-semibold mb-2">⚠️ {t('Başvuru Durumu')}</div>
                    <div className="text-yellow-700 text-sm">{t('Başvurunuz onay sürecindedir')}</div>
                  </div>
                  <button className="mt-4 w-full px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-lg shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition text-base" onClick={() => {
                    setShowBayiModal(false);
                    setBayiStep(1);
                    setBayiForm({});
                    setBayiSozlesmeOnay(false);
                    setBayiFiles([]);
                    setBayiTab(0);
                    setUlke('TR');
                    setSehir('');
                  }}>{t('kapat')}</button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Şifremi Unuttum Modalı */}
        {showForgotModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm flex flex-col gap-6 border border-[#e3e6ef] relative">
              <button className="absolute top-4 right-4 text-[#143b6b] text-2xl font-bold" onClick={() => { setShowForgotModal(false); setForgotSent(false); setForgotEmail(""); }}>&times;</button>
              {!forgotSent ? (
                <form onSubmit={async (e) => { 
                  e.preventDefault(); 
                  setError('');
                  try {
                    const res = await fetch('/api/admin/reset-password', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: forgotEmail })
                    });
                    if (res.ok) {
                      setForgotSent(true);
                    } else {
                      setForgotSent(true);
                    }
                  } catch {
                    setForgotSent(true);
                  }
                }} className="flex flex-col gap-4">
                  <div className="text-xl font-bold text-[#143b6b] mb-2">{t('sifreSifirla')}</div>
                  <div className="text-[#4b587c] text-sm mb-2">{t('sifreSifirlaDesc')}</div>
                  <input
                    type="email"
                    className="w-full px-4 py-3 rounded-lg border border-[#e3e6ef] focus:ring-2 focus:ring-[#143b6b] text-[#1e2a44] transition placeholder:text-[#bfc9e0]"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    required
                    placeholder={t('sifreSifirlaDesc')}
                    autoFocus
                  />
                  <button type="submit" className="w-full px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-base shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition">{t('gonder')}</button>
                </form>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <div className="text-xl font-bold text-[#143b6b]">{t('talimatGonderildi')}</div>
                  <div className="text-[#4b587c] text-center">{t('sentDesc')}</div>
                  <button className="mt-2 w-full px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-base shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition" onClick={() => { setShowForgotModal(false); setForgotSent(false); setForgotEmail(""); }}>{t('kapat')}</button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Nasıl Giriş Yapabilirim Modalı */}
        {showHelpModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col gap-6 border border-[#e3e6ef] relative">
              <button className="absolute top-4 right-4 text-[#143b6b] text-2xl font-bold" onClick={() => setShowHelpModal(false)}>&times;</button>
              <div className="text-xl font-bold text-[#143b6b] mb-2">{t('helpTitle')}</div>
              <ul className="list-disc pl-6 text-[#4b587c] text-sm space-y-2">
                <li>{t('helpList.0') || 'INVAMED partner paneline giriş yapmak için kayıtlı e-posta adresinizi ve şifrenizi girin.'}</li>
                <li>{t('helpList.1') || 'Demo kullanıcı ile giriş yapmak için:'}</li>
                <li>{t('helpList.2') || 'Şifrenizi unuttuysanız "Şifremi Unuttum" bağlantısını kullanarak sıfırlama talebinde bulunabilirsiniz.'}</li>
                <li>{t('helpList.3') || 'Girişte sorun yaşıyorsanız, e-posta adresinizi ve şifrenizi doğru girdiğinizden emin olun. Hala sorun devam ediyorsa destek ekibimizle iletişime geçin.'}</li>
              </ul>
              <button className="mt-2 w-full px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-base shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition" onClick={() => setShowHelpModal(false)}>{t('kapat')}</button>
            </div>
          </div>
        )}
        {/* Partnerlik Sözleşmesi Modalı */}
        {showSozlesmeModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-0 w-full max-w-4xl max-h-[90vh] flex flex-col border border-[#e3e6ef] relative overflow-hidden">
              <div className="flex-shrink-0 px-8 pt-8 pb-4 border-b border-[#e3e6ef]">
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-[#143b6b]">{t('partnerlikSozlesmeTitle')}</div>
                  <button 
                    className="text-[#143b6b] text-2xl font-bold hover:text-[#1e2a44] transition" 
                    onClick={() => setShowSozlesmeModal(false)}
                  >
                    &times;
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-8 py-6">
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-[#1e2a44] leading-relaxed">
                    {t('partnerlikSozlesmeText')}
                  </pre>
                </div>
              </div>
              <div className="flex-shrink-0 px-8 pb-8 pt-4 border-t border-[#e3e6ef]">
                <button 
                  className="w-full px-4 py-3 rounded-lg bg-[#143b6b] text-white font-bold text-base shadow-lg hover:bg-[#1e2a44] hover:text-white border border-[#143b6b] transition" 
                  onClick={() => setShowSozlesmeModal(false)}
                >
                  {t('sozlesmeyiKapat')}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Navbar dışında dil dropdown açıkken dışarı tıklanınca kapansın */}
        {langDropdown && <div className="fixed inset-0 z-40" onClick={() => setLangDropdown(false)}></div>}
      </div>
      
      {/* Ürün kataloğu ve istatistikler kaldırıldı */}
      {/* Partnerlik ve Connect Now birleşik bölüm */}
      <div className="w-full flex flex-col items-center justify-center px-2 pt-12 pb-16 mt-8 relative"
        style={{
          background: 'linear-gradient(135deg, #e3f0ff 0%, #fafdff 60%, #f3f4f6 100%)',
        }}>
        {/* Üst: Partnerlik metni */}
        <div className="w-full max-w-3xl flex flex-col items-center justify-center h-full mb-12">
          <div className="text-[#143b6b] font-light text-2xl md:text-3xl mb-4 text-center">{t('globalPartnersTitle')}</div>
          <div className="text-[#4b587c] text-center text-base md:text-lg max-w-2xl">{t('globalPartnersDesc')}</div>
        </div>
        {/* Alt: Connect Now ve slider */}
        <div className="w-full flex flex-col md:flex-row items-center justify-center gap-8">
          {/* Sol: Başlık, açıklama, buton */}
          <div className="flex-1 flex flex-col items-start justify-center max-w-xl">
            <div className="text-2xl md:text-3xl font-bold text-[#143b6b] mb-3">{t('Connect Now')}</div>
            <div className="text-[#4b587c] text-base md:text-lg mb-6">{t('Tüm bilgilere tek bir yerden erişebilmek için ücretsiz kaydolun.')}</div>
                          <button className="px-6 py-3 rounded-lg border-2 border-[#143b6b] text-[#143b6b] font-bold text-lg bg-white hover:bg-[#143b6b] hover:text-white transition shadow-none" onClick={() => navigate('/teklif-olustur')}>{t('Connect Free!')}</button>
          </div>
          {/* Sağ: Portal ekran görüntüsü slider alanı */}
          <div className="flex-1 flex items-center justify-center w-full max-w-2xl cursor-pointer" onClick={() => navigate('/teklif-olustur')}>
            <div className="w-full bg-white rounded-2xl shadow-lg border border-[#e3e6ef] overflow-hidden hover:shadow-2xl hover:scale-[1.02] transition-all duration-300">
              <img 
                src="/sliderekran.jpg" 
                alt="INVAMED Portal Ekran Görüntüsü" 
                className="w-full h-auto object-contain rounded-2xl"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="w-full bg-white py-6 flex items-center justify-center text-center mt-0">
        <span className="text-xs text-[#bfc9e0]">© {new Date().getFullYear()} INVAMED Portal. All rights reserved.</span>
      </footer>
      {/* Teklif Talebi Oluşturma Formu: Popup Modal */}
      {showTeklifTalepForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-blue-100/80 via-white/90 to-blue-200/80 backdrop-blur-[2px] animate-fade-in">
          <button
            className="absolute top-8 right-12 text-blue-900 text-4xl font-light z-20 hover:text-red-600 transition rounded-full bg-white/80 shadow-lg w-12 h-12 flex items-center justify-center border border-blue-100"
            onClick={() => setShowTeklifTalepForm(false)}
            aria-label="Kapat"
          >
            &times;
          </button>
          <div className="w-full max-w-5xl mx-auto pt-10 pb-8 px-2 sm:px-6 flex flex-col min-h-[80vh] rounded-3xl shadow-2xl border border-blue-100 bg-white/90 relative">
            <div className="text-3xl font-light text-blue-900 mb-8 text-center tracking-tight">Teklif Talebi Oluştur</div>
            <form onSubmit={handleTeklifTalepSubmit} className="flex flex-col h-full">
              {/* Sadece tabloyu saran div scrollable olacak */}
              <div className="w-full min-h-[180px] max-h-[420px] md:max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-blue-200 scrollbar-track-blue-50 relative flex-1">
                <table className="min-w-full bg-white">
                    <thead className="sticky top-0 z-20">
                      <tr className="bg-blue-50">
                        <th className="px-4 py-3 w-[220px] text-left font-semibold rounded-tl-lg rounded-bl-lg">Ürün</th>
                        <th className="px-4 py-3 w-[140px] text-left font-semibold">Ölçü</th>
                        <th className="px-4 py-3 w-[100px] text-left font-semibold">Miktar</th>
                        <th className="px-4 py-3 w-[220px] text-left font-semibold">Açıklama</th>
                        <th className="px-2 py-3 w-[60px] rounded-tr-lg rounded-br-lg"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {talepRows.map((row, idx) => {
                        const product = products.find(p => p.id === Number(row.productId));
                        const measureOptions = product ? (product.measures || ['Standart']) : [];
                        return (
                          <tr key={idx} className="transition hover:bg-blue-50/40 group bg-white even:bg-gray-50 my-[10px] rounded-lg">
                                {/* Ürün seçici */}
                                <td className="align-middle w-[220px]">
                                  <div className="relative">
                                    <button
                                      type="button"
                                      className="w-full px-2 py-2 h-12 rounded-none border border-[#e3e6ef] bg-white text-left flex items-center gap-2 focus:ring-2 focus:ring-blue-400 shadow-sm group-hover:border-blue-400 transition"
                                      onClick={() => setTalepRows(talepRows.map((r, i) => i === idx ? { ...r, showDropdown: true } : { ...r, showDropdown: false }))}
                                    >
                                      {row.productId ? (
                                        <>
                                          <img src={`/urunler/${productImageSlug(products.find(p => p.id === Number(row.productId))?.name || '')}.jpg`} alt="" className="w-8 h-8 object-contain rounded mr-2 border border-[#e3e6ef] bg-white" />
                                          <span className="truncate max-w-[120px] inline-block align-middle font-semibold text-blue-900">{products.find(p => p.id === Number(row.productId))?.name}</span>
                                        </>
                                      ) : <span className="text-gray-400">Ürün seçin...</span>}
                                      <svg className="w-4 h-4 ml-auto text-gray-400 group-hover:text-blue-500 transition" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                    {/* Popup ürün seçici */}
                                    {row.showDropdown && (
                                      <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30" onClick={e => { if (e.target === e.currentTarget) setTalepRows(talepRows.map((r, i) => i === idx ? { ...r, showDropdown: false } : r)); }}>
                                        <div className="mt-24 bg-white rounded-2xl shadow-2xl border border-blue-200 p-4 w-full max-w-lg max-h-[70vh] overflow-y-auto flex flex-col gap-2">
                                          <div className="text-lg font-bold text-blue-900 mb-2 text-center">Ürün Seç</div>
                                          {products.map(p => (
                                            <button
                                              type="button"
                                              key={p.id}
                                              className={`flex items-center gap-3 w-full px-3 py-2 rounded-xl hover:bg-blue-100 border border-transparent ${row.productId === p.id ? 'bg-blue-200 border-blue-400' : ''}`}
                                              onClick={() => {
                                                handleRowChange(idx, 'productId', p.id);
                                              }}
                                            >
                                              <img src={`/urunler/${productImageSlug(p.name)}.jpg`} alt={p.name} className="w-12 h-12 object-contain rounded border border-[#e3e6ef] bg-white" />
                                              <div className="flex flex-col items-start">
                                                <span className="font-semibold text-blue-900 text-base">{p.name}</span>
                                                <span className="text-xs text-gray-500 truncate max-w-[200px]">{p.description}</span>
                                              </div>
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                {/* Ölçü seçici */}
                                <td className="align-middle w-[140px]">
                                  <select
                                    className="w-full px-2 py-2 h-12 rounded-none border border-[#e3e6ef] focus:ring-2 focus:ring-blue-400 text-blue-900 bg-white shadow-sm group-hover:border-blue-400 transition"
                                    value={row.measure}
                                    onChange={e => handleRowChange(idx, 'measure', e.target.value)}
                                    required
                                    disabled={!row.productId}
                                  >
                                    <option value="">Ölçü seçin...</option>
                                    {measureOptions.map(opt => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                </td>
                                {/* Miktar */}
                                <td className="align-middle w-[100px]">
                                  <input
                                    type="number"
                                    min="1"
                                    className="w-full px-2 py-2 h-12 rounded-none border border-[#e3e6ef] focus:ring-2 focus:ring-blue-400 text-blue-900 bg-white shadow-sm group-hover:border-blue-400 transition"
                                    value={row.qty}
                                    onChange={e => handleRowChange(idx, 'qty', e.target.value)}
                                    required
                                  />
                                </td>
                                {/* Açıklama */}
                                <td className="align-middle w-[220px]">
                                  <input
                                    type="text"
                                    className="w-full px-2 py-2 h-12 rounded-none border border-[#e3e6ef] focus:ring-2 focus:ring-blue-400 text-blue-900 bg-white shadow-sm group-hover:border-blue-400 transition"
                                    value={row.desc}
                                    onChange={e => handleRowChange(idx, 'desc', e.target.value)}
                                    placeholder="Açıklama"
                                  />
                                </td>
                                {/* Sil butonu */}
                                <td className="align-middle w-[60px] pr-2">
                                  <button
                                    type="button"
                                    className="w-11 h-11 rounded-full bg-red-100 border border-red-200 text-red-600 flex items-center justify-center shadow-md hover:bg-red-600 hover:text-white transition ml-auto"
                                    onClick={() => handleRemoveRow(idx)}
                                    title="Satırı Sil"
                                  >
                                    <HiOutlineTrash className="w-6 h-6" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
  
        
            <div className="flex justify-between items-center mt-6 gap-4">
              <button type="button" className="px-4 py-2 rounded-lg bg-blue-100 text-blue-800 font-semibold border border-blue-200 hover:bg-blue-200 transition" onClick={handleAddRow}>+ Satır Ekle</button>
              <button type="submit" className="px-6 py-3 rounded-lg bg-blue-700 text-white font-bold text-lg shadow-lg hover:bg-blue-800 border border-blue-700 transition">Talep Gönder</button>
            </div>
          </form>
        </div>
        <style>{`
          @keyframes fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
          .animate-fade-in { animation: fade-in 0.2s ease; }
        `}</style>
      </div>
    )}
  {/* Şifre Değiştirme Modalı (Geçici Şifre) */}
  {showPasswordChange && (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" style={{backdropFilter:'blur(4px)'}}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-blue-100 overflow-hidden">
        <div className="bg-gradient-to-r from-[#0d2b52] to-[#1a4a8a] p-6 text-center">
          <div className="w-14 h-14 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-3">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>
          </div>
          <h2 className="text-xl font-bold text-white">Şifre Değiştirme</h2>
          <p className="text-blue-200 text-sm mt-1">Güvenliğiniz için geçici şifrenizi değiştirmeniz gerekmektedir.</p>
        </div>
        <form className="p-6 space-y-4" onSubmit={async e => {
          e.preventDefault();
          setPwChangeError('');
          if (!pwChangeNew || !pwChangeConfirm) { setPwChangeError('Tüm alanları doldurun.'); return; }
          if (pwChangeNew.length < 6) { setPwChangeError('Yeni şifre en az 6 karakter olmalıdır.'); return; }
          if (pwChangeNew !== pwChangeConfirm) { setPwChangeError('Şifreler eşleşmiyor.'); return; }
          if (password === pwChangeNew) { setPwChangeError('Yeni şifre mevcut şifreden farklı olmalıdır.'); return; }
          setPwChangeLoading(true);
          try {
            const authToken = pwChangePendingUser?.token || localStorage.getItem('authToken') || '';
            const res2 = await fetch('/api/change-password', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
              body: JSON.stringify({ email, current_password: password, new_password: pwChangeNew })
            });
            const data2 = await res2.json();
            if (data2.success && pwChangePendingUser) {
              const u = { ...pwChangePendingUser };
              delete u.must_change_password;
              if (u.token) localStorage.setItem("authToken", u.token);
              localStorage.setItem("user", JSON.stringify(u));
              if (onLogin) onLogin({ ...u, role: u.rol });
              if (login) login({ ...u, role: u.rol });
              setShowPasswordChange(false);
              navigate("/dash");
            } else {
              setPwChangeError(data2.error || 'Şifre değiştirilemedi.');
            }
          } catch { setPwChangeError('Sunucuya bağlanılamadı.'); }
          setPwChangeLoading(false);
        }}>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-800 leading-relaxed">
              <strong>Geçici şifre ile giriş yaptınız.</strong> Devam etmek için yeni bir şifre belirlemeniz zorunludur. 3 gün içinde şifre değiştirilmezse hesabınız pasif moda alınacaktır.
            </p>
          </div>
          {pwChangeError && (
            <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs text-center">{pwChangeError}</div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">E-posta</label>
            <input type="text" value={email} readOnly className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Yeni Şifre</label>
            <input type="password" value={pwChangeNew} onChange={e => setPwChangeNew(e.target.value)} placeholder="En az 6 karakter"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400" required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Yeni Şifre (Tekrar)</label>
            <input type="password" value={pwChangeConfirm} onChange={e => setPwChangeConfirm(e.target.value)} placeholder="Şifreyi tekrar girin"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400" required />
          </div>
          <button type="submit" disabled={pwChangeLoading}
            className="w-full py-3 bg-gradient-to-r from-[#0d2b52] to-[#1a4a8a] text-white rounded-xl font-bold text-sm hover:from-[#0a2244] hover:to-[#153d73] transition shadow-md disabled:opacity-50">
            {pwChangeLoading ? 'Değiştiriliyor...' : 'Şifremi Değiştir ve Giriş Yap'}
          </button>
        </form>
      </div>
    </div>
  )}
  </>
);
} 